import { InspectionStatus } from "src/data/types";

export type LOADING_STATE = "idle" | "pending" | "fulfilled" | "rejected";

export const DEFAULT_LOCALE = "en";
export const HOMEPAGE_MESSAGE_BUNDLE_NAME = "homepage";

// LDAP Groups for WHSEAP
export const WHS_DATA_EAP_USER_LDAP_GROUP = "whs-data-eap-user";
export const WHS_DATA_EAP_ADMIN_LDAP_GROUP = "whs-data-eap-admin";
// Kept for dev-mode detection only (isEngineerUser); grants no access permission.
export const WORKINGWELL_ENGINEER_LDAP_GROUP = "workingwell-engineer";

// Corporate users: membership in any grants access.
export const CORPORATE_LDAP_GROUPS = [
  WHS_DATA_EAP_USER_LDAP_GROUP,
  WHS_DATA_EAP_ADMIN_LDAP_GROUP,
];

// Pilot-site feature groups (classification / inspection).
export const WHS_SAFETYGENIE_CLASSIFICATION_LDAP_GROUP =
  "whs-safetygenie-classification";
export const WHS_SAFETYGENIE_INSPECTION_LDAP_GROUP =
  "whs-safetygenie-inspection";
export const FEATURE_LDAP_GROUPS = [
  WHS_SAFETYGENIE_CLASSIFICATION_LDAP_GROUP,
  WHS_SAFETYGENIE_INSPECTION_LDAP_GROUP,
];

// Pilot-site location groups (the 5 pilot sites).
export const SITE_LDAP_GROUPS = [
  "whs-safetygenie-bfi4",
  "whs-safetygenie-bna5",
  "whs-safetygenie-ftw6",
  "whs-safetygenie-mdw2",
  "whs-safetygenie-mqy1",
];

// Corporate user: in whs-data-eap-user or whs-data-eap-admin.
export const isCorporateUser = (ldapGroups: string[]): boolean =>
  ldapGroups.some((group) => CORPORATE_LDAP_GROUPS.includes(group));

// Pilot-site user: in a feature group (classification/inspection) AND a site group.
export const isPilotSiteUser = (ldapGroups: string[]): boolean =>
  ldapGroups.some((group) => FEATURE_LDAP_GROUPS.includes(group)) &&
  ldapGroups.some((group) => SITE_LDAP_GROUPS.includes(group));

// Access is granted to corporate users OR pilot-site users.
export const hasValidPermissions = (ldapGroups: string[]): boolean =>
  isCorporateUser(ldapGroups) || isPilotSiteUser(ldapGroups);

// Check if user is an engineer (has dev mode access)
export const isEngineerUser = (ldapGroups: string[]): boolean => {
  return ldapGroups.some((group) => group === WORKINGWELL_ENGINEER_LDAP_GROUP);
};

export const ADD_DETAILS = "add-details";
export const ADD_QUESTIONS = "add-questions";
export const AI = "AI";
export const MANUAL_OVERRIDE = "Manual Override";
export const NON_COMPLIANT = "non_compliant";
export const COMPLIANT = "compliant";
export const COMPLIANT_LABEL = "Compliant";
export const NON_COMPLIANT_LABEL = "Non Compliant";
export const NA_LABEL = "N/A";
export const compliantStatus = {
  YES: "Yes",
  NO: "No",
};
export const inProgressInspectionStatus = "In_Progress";
export const completedInspectionStatus = "Completed";

export const DEFAULT_SITE_CODE = "BFI4";

/** EAP pilot sites — sorted alphabetically by code, once at module load. */
export const EAP_PILOT_SITES: { code: string; label: string }[] = [
  { code: "BFI4", label: "BFI4, Washington" },
  { code: "BFI9", label: "BFI9, Washington" },
  { code: "BHM1", label: "BHM1, Alabama" },
  { code: "BNA3", label: "BNA3, Tennessee" },
  { code: "BNA5", label: "BNA5, Tennessee" },
  { code: "DEMO1", label: "DEMO1, Demo Site" },
  { code: "DEN8", label: "DEN8, Colorado" },
  { code: "EIK2", label: "EIK2, Colorado" },
  { code: "FTW6", label: "FTW6, Texas" },
  { code: "MDW2", label: "MDW2, Illinois" },
  { code: "MQY1", label: "MQY1, Tennessee" },
  { code: "OAK3", label: "OAK3, California" },
  { code: "PAE2", label: "PAE2, Washington" },
].sort((a, b) => a.code.localeCompare(b.code));

/**
 * Returns the sites a user may see/select in the site dropdown:
 * - Corporate users: all sites.
 * - Pilot-site users: only the sites their `whs-safetygenie-{code}` groups grant.
 * A site group maps to a site code by uppercasing the suffix
 * (e.g. "whs-safetygenie-mqy1" -> "MQY1").
 */
export const getVisibleSites = (
  ldapGroups: string[]
): { code: string; label: string }[] => {
  if (isCorporateUser(ldapGroups)) return EAP_PILOT_SITES;
  const authorizedCodes = ldapGroups
    .filter((group) => SITE_LDAP_GROUPS.includes(group))
    .map((group) => group.replace("whs-safetygenie-", "").toUpperCase());
  return EAP_PILOT_SITES.filter((site) => authorizedCodes.includes(site.code));
};

/**
 * Parses a siteLocation string into a readable format: "BAN12, NASHVILLE, TN".
 *
 * Input format: "BNA12-CO(NASHVILLE,TN,US)"
 * The regex /\(([^)]+)\)/ captures the content inside parentheses —
 * i.e. "NASHVILLE,TN,US" — which is then split into city and state and country(if needed).
 */
export const parseSiteName = (siteLocation: string): string => {
  if (!siteLocation) return DEFAULT_SITE_CODE;

  const siteCode = siteLocation.split("-")[0].trim();
  const match = siteLocation.match(/\(([^)]+)\)/);
  if (!match) return siteCode;
  const [city, state] = match[1].split(",");
  return `${siteCode}, ${city.trim()}, ${state.trim()}`;
};
